/**
 * Host guide beside the pages (#1165): which host page is open, which preset
 * questions fit it, and where an action proposal deep-links.
 *
 * Pure module, unit-tested in hostGuide.test.ts. Two rules from the issue:
 * - preset buttons SEND A FIXED MESSAGE, they never call an action;
 * - an action card only navigates to the real UI's own confirm step. Nothing is
 *   sent, shared, closed or scheduled from the chat itself.
 * Exercise words never appear here: presets are generic by construction.
 */

export type HostPageKind = 'session' | 'exercise' | 'programme' | 'study' | 'home' | 'other';

export type HostPage = { kind: HostPageKind; id?: string };

const OBJECT_ID = /^[a-f0-9]{24}$/i;

/** Null outside the host area (the guide panel is a host-area tool). */
export const resolveHostPage = (pathname: string): HostPage | null => {
    const path = pathname.replace(/\/+$/, '');
    if (!(path === '/host' || path.startsWith('/host/'))) {
        return null;
    }
    const segments = path.split('/').filter(Boolean); // ['host', ...]
    const idAt = (index: number): string | undefined => {
        const candidate = segments[index];
        return candidate && OBJECT_ID.test(candidate) ? candidate.toLowerCase() : undefined;
    };
    switch (segments[1]) {
        case 'sessions':
            return idAt(2) ? { kind: 'session', id: idAt(2) } : { kind: 'home' };
        case 'exercises':
            return idAt(2) ? { kind: 'exercise', id: idAt(2) } : { kind: 'home' };
        case 'programmes':
            return idAt(2) ? { kind: 'programme', id: idAt(2) } : { kind: 'home' };
        case 'studies':
            return idAt(2) ? { kind: 'study', id: idAt(2) } : { kind: 'home' };
        case undefined:
        case 'home':
            return { kind: 'home' };
        default:
            return { kind: 'other' };
    }
};

/**
 * A preset either sends a fixed chat message (`message`) or opens a page (`to`),
 * never both. Labels and messages are translation keys.
 */
export type GuidePreset =
    | { key: string; labelKey: string; messageKey: string }
    | { key: string; labelKey: string; to: string };

/** The setup paths every host page offers: manual (host console) and agent-assisted (/start). */
const setupPresets = (page: HostPage): GuidePreset[] => {
    const manual =
        page.kind === 'session' && page.id
            ? `/host/sessions/${page.id}?section=setup`
            : page.kind === 'exercise' && page.id
              ? `/host/exercises/${page.id}?edit=setup`
              : page.kind === 'programme' && page.id
                ? `/host/programmes/${page.id}`
                : page.kind === 'study' && page.id
                  ? `/host/studies/${page.id}`
                  : '/host/create-session';
    return [
        { key: 'setup_manual', labelKey: 'guide.preset.setupManual', to: manual },
        { key: 'setup_agent', labelKey: 'guide.preset.setupAgent', to: '/start' },
    ];
};

/** 3 to 5 buttons derived from the open page. */
export const presetsForPage = (page: HostPage | null): GuidePreset[] => {
    if (!page) {
        return [];
    }
    switch (page.kind) {
        case 'session':
        case 'exercise':
            return [
                { key: 'unanswered', labelKey: 'guide.preset.unanswered', messageKey: 'guide.preset.unansweredMessage' },
                { key: 'explain', labelKey: 'guide.preset.explainReading', messageKey: 'guide.preset.explainReadingMessage' },
                { key: 'plan', labelKey: 'guide.preset.planNext', messageKey: 'guide.preset.planNextMessage' },
                ...setupPresets(page),
            ];
        case 'programme':
            return [
                { key: 'unanswered', labelKey: 'guide.preset.unansweredRound', messageKey: 'guide.preset.unansweredRoundMessage' },
                { key: 'explain', labelKey: 'guide.preset.explainLatest', messageKey: 'guide.preset.explainLatestMessage' },
                { key: 'plan', labelKey: 'guide.preset.planNext', messageKey: 'guide.preset.planNextMessage' },
                ...setupPresets(page),
            ];
        case 'study':
            return [
                { key: 'progress', labelKey: 'guide.preset.studyProgress', messageKey: 'guide.preset.studyProgressMessage' },
                { key: 'explain', labelKey: 'guide.preset.explainLatest', messageKey: 'guide.preset.explainLatestMessage' },
                ...setupPresets(page),
            ];
        default:
            return [
                { key: 'attention', labelKey: 'guide.preset.attention', messageKey: 'guide.preset.attentionMessage' },
                { key: 'open', labelKey: 'guide.preset.stillOpen', messageKey: 'guide.preset.stillOpenMessage' },
                ...setupPresets(page),
            ];
    }
};

export const CHAT_ACTION_KINDS = ['remind_unanswered', 'share_reading', 'close_exercise', 'schedule_next', 'open_invite'] as const;
export type ChatActionKind = (typeof CHAT_ACTION_KINDS)[number];
export type ChatAction = { kind: ChatActionKind; targetId: string };

/** Only well-formed, on-list proposals become cards; the backend already filters, this is the belt to its braces. */
export const sanitizeChatActions = (raw: unknown): ChatAction[] => {
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw.filter(
        (item): item is ChatAction =>
            !!item
            && typeof item === 'object'
            && (CHAT_ACTION_KINDS as readonly string[]).includes((item as ChatAction).kind)
            && typeof (item as ChatAction).targetId === 'string'
            && OBJECT_ID.test((item as ChatAction).targetId),
    );
};

/**
 * Where a card lands. Every target is an existing host page whose own button or
 * confirm dialog does the deed; `guide=` tells that page which step to open.
 */
export const actionTarget = (action: ChatAction): { to: string; labelKey: string } => {
    const id = action.targetId;
    switch (action.kind) {
        case 'remind_unanswered':
            return { to: `/host/sessions/${id}?section=invite&guide=remind`, labelKey: 'guide.action.remind' };
        case 'share_reading':
            return { to: `/host/exercises/${id}?guide=share`, labelKey: 'guide.action.share' };
        case 'close_exercise':
            return { to: `/host/sessions/${id}?guide=close`, labelKey: 'guide.action.close' };
        case 'schedule_next':
            return { to: `/host/sessions/${id}?section=summaries&guide=schedule`, labelKey: 'guide.action.schedule' };
        case 'open_invite':
            return { to: `/host/exercises/${id}/invite`, labelKey: 'guide.action.invite' };
    }
};
